import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { Auth } from 'aws-amplify';
import { SessionService } from './session.service';
import { AuthService } from './auth.service';
import { LoaderService } from './loader.service';

@Injectable()
export class MyInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  constructor(
    private sessionService: SessionService,
    private authService: AuthService,
    private loaderService: LoaderService,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    // ✅ Skip Authorization for specific APIs
    if (request.headers.get('Skip-Auth') === 'true') {
      const cleanHeaders = request.headers.delete('Skip-Auth');
      return next.handle(request.clone({ headers: cleanHeaders }));
    }

    // Only JSON - API Like GET POST
    if (request.responseType != "json" && request.responseType != "text") return next.handle(request);

    const skipLoader = request.url.includes('api/config-new') || request.url.includes('api/hoc-process/get-sqs-send-status') || request.url.includes('api/hoc-process/get-extract-validation-errors') || request.url.includes('api/hoc-process/send-sqs-message') || request.url.includes('api/hoc-process/get-hoc-log-messages');
    if (!skipLoader) this.loaderService.show();

    const hideLoader = () => { if (!skipLoader) this.loaderService.hide(); };

    // Checking User
    if (!this.authService.isLoggedIn()) {
      return next.handle(request).pipe(finalize(hideLoader));
    }

    return from(Auth.currentSession()).pipe(
      switchMap((session) => {
        if (session.isValid()) {
          const token = session.getIdToken().getJwtToken();
          return this.handleRequestWithToken(request, next, token);
        } else {
          return throwError('Unauthorized');
        }
      }),
      catchError((error: HttpErrorResponse | any) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }

        // when No current user
        if (error == 'No current user') {
          this.authService.logoutAndRedirect();
        }
        if (error.message == 'No current user') {
          this.authService.logoutAndRedirect();
        }

        return throwError(error);
      }),
      finalize(hideLoader)
    );
  }

  private handleRequestWithToken(request: HttpRequest<unknown>, next: HttpHandler, token: string): Observable<HttpEvent<unknown>> {
    if (this.authService.isLoggedIn()) {
      let headers = request.headers;

      // Add region/environment headers if available
      if (this.sessionService.getStoreData('envHeaderVariables')) {
        const duEnv: any = this.sessionService.getStoreData('envHeaderVariables');
        for (let key in duEnv) {
          headers = headers.set(key, duEnv[key].toString());
        }
      }

      // Always set Authorization and user_id
      headers = headers.set('Authorization', `Bearer ${token}`);
      headers = headers.set('user_id', this.sessionService.getUsername());

      // ✅ Only set 'Content-Type' if not explicitly skipped
      const skipContentType = headers.get('Skip-Content-Type') === 'true';
      if (!skipContentType) {
        headers = headers.set('Content-Type', 'application/json');
      }

      // Remove custom hint header before sending the request
      headers = headers.delete('Skip-Content-Type');

      request = request.clone({ headers });
    }

    return next.handle(request);
  }


  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(Auth.currentSession()).pipe(
        switchMap((session) => {
          const token = session.getIdToken().getJwtToken();
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          return this.handleRequestWithToken(request, next, token);
        }),
        catchError((error) => {
          // Handle refresh token failure (e.g., redirect to login page)
          return throwError('Unauthorized');
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          return this.handleRequestWithToken(request, next, this.sessionService.getUsername());
        })
      );
    }
  }
}
